'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { ApiClientError } from '@/shared/lib/api';
import { useNestedSheetStack } from '@/shared/ui';
import { ProjectGoalsSheet } from '@/features/project/ui/ProjectGoalsSheet';
import {
  useConceptEstimates,
  useEstimateActions,
  useEstimateDetail,
  selectCurrentEstimate,
} from '../hooks/use-estimate';
import { useEstimateGoalsSheet } from '../hooks/use-estimate-goals-sheet';
import { useEstimateIntro } from '../hooks/use-estimate-intro';
import {
  EstimateSettingsList,
  toEstimateSettingsValues,
} from './EstimateHeader';
import { EstimateDraftSettings } from './EstimateDraftSettings';
import { COST_ESTIMATE_OVERWRITE_REQUIRED } from '../api/estimate-service';

interface EstimateSettingsPanelProps {
  projectId: string;
  conceptId: string;
}

/**
 * Estimate settings inspector for the two-column layout: lives in the
 * trailing inspector aside (Design/Album-tab parity) while the estimate
 * document fills the content column. Hosts the staleness banner + the
 * settings list (Region / Currency / Units / Facade area with inline
 * divergence warnings) and the Regenerate CTA pinned to the panel footer —
 * the stacked (single-column) layout renders all of these inline in the
 * editor flow instead (EstimateEditor).
 *
 * Shares the estimate queries with EstimateTabContent/EstimateEditor (same
 * cache keys — deduped), so settings edits and regenerate reflect in the
 * content column immediately. Renders nothing for the intro / generating /
 * failed states — those span the whole content area (nothing to configure).
 */
export function EstimateSettingsPanel({
  projectId,
  conceptId,
}: EstimateSettingsPanelProps) {
  const t = useTranslations('common.estimate.editor');

  // Drill-down chrome parity with the render inspector (RenderPanelContent):
  // while a sub-panel (goals / currency NestedSheet) is open it owns the
  // column, so the pinned Regenerate footer must not paint under/over it.
  const { hasOpenNestedSheet, setPortalContainer } = useNestedSheetStack();

  const { data: estimates } = useConceptEstimates(projectId, conceptId);
  const currentEstimate = selectCurrentEstimate(estimates);
  const { data: estimate } = useEstimateDetail(currentEstimate?.id);

  // Working intro (no estimate yet, entitled user): the inspector hosts the
  // DRAFT settings + Generate while the content column shows the hero
  // (EstimateTabContent). Same query keys as the tab content — deduped.
  const { data: intro } = useEstimateIntro(projectId, conceptId);

  const {
    regenerateEstimate,
    isRegenerating,
    updateEstimate,
    changeLanguage,
    isChangingLanguage,
  } = useEstimateActions(projectId, conceptId);

  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  // The region/facade-area rows open the shared project-goals sheet so the
  // user stays on the Estimate tab (close → flush + estimate re-fetch — see
  // the hook).
  const { showGoalsSheet, handleGoalsSheetChange } = useEstimateGoalsSheet(
    projectId,
    estimate?.id
  );

  const runRegenerate = async (confirmOverwrite?: boolean) => {
    if (!estimate) return;
    try {
      await regenerateEstimate({ estimateId: estimate.id, confirmOverwrite });
      setShowOverwriteConfirm(false);
    } catch (error) {
      // Edited estimate → server demands explicit confirmation (409).
      if (
        error instanceof ApiClientError &&
        error.code === COST_ESTIMATE_OVERWRITE_REQUIRED
      ) {
        setShowOverwriteConfirm(true);
        return;
      }
      // Other errors already surfaced as a toast in the mutation onError.
    }
  };

  // No estimate yet → the WORKING intro: draft settings + Generate in the
  // inspector (the content column shows the hero). Waits for the estimate list
  // (`estimates`) so the panel can't flash before a just-created estimate is
  // known, and for the intro (draft defaults). No client-side plan gate
  // post-pivot — Generate POSTs the draft and the server returns the estimate
  // paywall (PackRequired) for non-pack users.
  if (estimates && !currentEstimate) {
    if (!intro) return null;
    return (
      <EstimateDraftSettings
        projectId={projectId}
        conceptId={conceptId}
        intro={intro}
        variant="panel"
      />
    );
  }

  // Only a READY estimate has settings to show; the pending / generating /
  // failed states own the whole content column and need no inspector tools.
  if (!estimate || estimate.status !== 'ready') {
    return null;
  }

  // The display switches (currency/units) moved to the document-title tags —
  // EstimateEditor fires and locks for them; the inspector only owns the
  // generation inputs below.
  const isBusy = isRegenerating || isChangingLanguage;

  return (
    /* `ref={setPortalContainer}` + `relative` registers this as the drill-down
       column so the NestedSheets (goals / currency / language / requirements)
       portal in and fill it — render-settings parity (RenderPanelContent
       anchors its sub-panels the same way). */
    <div ref={setPortalContainer} className="relative flex h-full flex-col">
      <div className="min-h-0 flex-1 flex flex-col gap-3 overflow-y-auto pb-2">
        {/* Server-computed staleness flag (`has_newer_renders`): not tied to
            a single setting, so it sits ABOVE the settings group
            (cross-platform placement). */}
        {estimate.has_newer_renders && (
          <NoticeBanner variant="warning" message={t('newerRendersWarning')} />
        )}

        <EstimateSettingsList
          values={toEstimateSettingsValues(estimate)}
          disabled={isBusy}
          onLanguageChange={(language) => changeLanguage(estimate.id, language)}
          isChangingLanguage={isChangingLanguage}
          onOpenProjectGoals={() => handleGoalsSheetChange(true)}
          onSpecialRequirementsSave={(value) =>
            updateEstimate(estimate.id, { special_requirements: value })
          }
        />
      </div>

      {/* Regenerate CTA pinned to the inspector footer (Design/Album parity:
          the tab's primary action lives next to its settings on the wide
          layout). Hidden while a drill-down sub-panel is open — the
          NestedSheet owns the whole column (Back + content only), same as the
          render inspector's Generate footer. */}
      {!hasOpenNestedSheet && (
        <div className="flex flex-shrink-0 justify-center pt-3 pb-4">
          <Button
            variant="outline"
            size="cta"
            onClick={() => runRegenerate()}
            disabled={isRegenerating}
          >
            {isRegenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('regenerate')}
          </Button>
        </div>
      )}

      {/* Shared project-goals editor (goals + region + facade area) — opened
          by the Region / Facade-area rows. */}
      <ProjectGoalsSheet
        projectId={projectId}
        open={showGoalsSheet}
        onOpenChange={handleGoalsSheetChange}
      />

      {/* Overwrite confirm — only shown when the estimate was hand-edited and
          the server requested explicit confirmation (409). */}
      <AlertDialog
        open={showOverwriteConfirm}
        onOpenChange={setShowOverwriteConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('overwriteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('overwriteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRegenerating}>
              {t('overwriteCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runRegenerate(true)}
              disabled={isRegenerating}
            >
              {t('overwriteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
